/**
 * Event Bus for Inter-Module Communication in LifeOS
 * 
 * Enables modules to communicate with each other through events
 * without tight coupling or direct dependencies.
 */

class EventBus {
  constructor() {
    this.events = {};
    this.middlewares = [];
    this.debugging = false;
    this.loggingMiddlewareAttached = false;
  }

  /**
   * Subscribe to an event
   * @param {string} eventName - Name of the event
   * @param {function} callback - Function to call when event is triggered
   * @param {object} options - Additional options
   * @returns {function} Unsubscribe function
   */
  on(eventName, callback, options = {}) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    const subscription = {
      callback,
      once: options.once || false,
      priority: options.priority || 0,
      module: options.module || 'unknown',
      id: Math.random().toString(36).substr(2, 9)
    };

    this.events[eventName].push(subscription);
    
    // Sort by priority (higher first)
    this.events[eventName].sort((a, b) => b.priority - a.priority);

    if (this.debugging) {
      console.log(`[EventBus] Subscribed to '${eventName}' from module '${subscription.module}'`);
    }

    // Return unsubscribe function
    return () => this.off(eventName, subscription.id);
  }

  /**
   * Subscribe to an event only once
   * @param {string} eventName - Name of the event
   * @param {function} callback - Function to call when event is triggered
   * @param {object} options - Additional options
   * @returns {function} Unsubscribe function
   */
  once(eventName, callback, options = {}) {
    return this.on(eventName, callback, { ...options, once: true });
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventName - Name of the event
   * @param {string} subscriptionId - ID of the subscription to remove
   */
  off(eventName, subscriptionId) {
    if (!this.events[eventName]) return;

    this.events[eventName] = this.events[eventName].filter(
      sub => sub.id !== subscriptionId
    );

    if (this.events[eventName].length === 0) {
      delete this.events[eventName];
    }

    if (this.debugging) {
      console.log(`[EventBus] Unsubscribed from '${eventName}' (${subscriptionId})`);
    }
  }

  /**
   * Emit an event to all subscribers
   * @param {string} eventName - Name of the event
   * @param {any} data - Data to pass to subscribers
   * @param {object} options - Additional options
   * @returns {Promise<Array>} Results from all subscribers
   */
  async emit(eventName, data = null, options = {}) {
    if (!this.events[eventName]) {
      if (this.debugging) {
        console.log(`[EventBus] No subscribers for event '${eventName}'`);
      }
      return [];
    }

    const event = {
      name: eventName,
      data,
      timestamp: new Date().toISOString(),
      source: options.source || 'unknown',
      ...options
    };

    // Run middlewares
    let processedEvent = event;
    for (const middleware of this.middlewares) {
      try {
        processedEvent = await middleware(processedEvent);
        if (!processedEvent) break; // Middleware can stop event propagation
      } catch (error) {
        console.error(`[EventBus] Middleware error:`, error);
      }
    }

    if (!processedEvent) return [];

    const subscribers = [...this.events[eventName]]; // Copy to avoid issues with modifications
    const results = [];

    if (this.debugging) {
      console.log(`[EventBus] Emitting '${eventName}' to ${subscribers.length} subscribers`);
    }

    for (const subscription of subscribers) {
      try {
        const result = await subscription.callback(processedEvent);
        results.push({
          module: subscription.module,
          result,
          success: true
        });

        // Remove one-time subscribers
        if (subscription.once) {
          this.off(eventName, subscription.id);
        }
      } catch (error) {
        console.error(`[EventBus] Error in subscriber for '${eventName}':`, error);
        results.push({
          module: subscription.module,
          error: error.message,
          success: false
        });
      }
    }

    return results;
  }

  /**
   * Add middleware to process events
   * @param {function} middleware - Function to process events
   */
  use(middleware) {
    if (typeof middleware !== 'function') {
      throw new Error('Middleware must be a function');
    }
    this.middlewares.push(middleware);
  }

  /**
   * Get all current subscriptions (for debugging)
   * @returns {object} Current subscriptions
   */
  getSubscriptions() {
    const subscriptions = {};
    for (const [eventName, subs] of Object.entries(this.events)) {
      subscriptions[eventName] = subs.map(sub => ({
        module: sub.module,
        priority: sub.priority,
        once: sub.once,
        id: sub.id
      }));
    }
    return subscriptions;
  }

  /**
   * Clear all subscriptions
   */
  clear() {
    this.events = {};
    if (this.debugging) {
      console.log('[EventBus] Cleared all subscriptions');
    }
  }

  /**
   * Enable or disable debugging
   * @param {boolean} enabled - Whether to enable debugging
   */
  setDebugging(enabled) {
    this.debugging = enabled;
    if (enabled && process.env.NODE_ENV === 'development') {
      this.ensureLoggingMiddleware();
    }
  }

  ensureLoggingMiddleware() {
    if (this.loggingMiddlewareAttached) return;
    this.use(async (event) => {
      console.log(`[EventBus] Event: ${event.name}`, {
        source: event.source,
        timestamp: event.timestamp,
        data: event.data
      });
      return event;
    });
    this.loggingMiddlewareAttached = true;
  }
}

// Create singleton instance
const eventBus = new EventBus();

// Add logging middleware when debugging explicitly enabled
if (eventBus.debugging && process.env.NODE_ENV === 'development') {
  eventBus.ensureLoggingMiddleware();
}

// Common event types for LifeOS modules
export const EventTypes = {
  // GPS Events
  GOAL_CREATED: 'gps:goal:created',
  GOAL_UPDATED: 'gps:goal:updated',
  GOAL_DELETED: 'gps:goal:deleted',
  GOAL_COMPLETED: 'gps:goal:completed',
  
  PRIORITY_CREATED: 'gps:priority:created',
  PRIORITY_UPDATED: 'gps:priority:updated',
  PRIORITY_DELETED: 'gps:priority:deleted',
  PRIORITY_COMPLETED: 'gps:priority:completed',
  
  STRATEGY_CREATED: 'gps:strategy:created',
  STRATEGY_UPDATED: 'gps:strategy:updated',
  STRATEGY_DELETED: 'gps:strategy:deleted',
  STRATEGY_COMPLETED: 'gps:strategy:completed',
  
  TASK_CREATED: 'gps:task:created',
  TASK_UPDATED: 'gps:task:updated',
  TASK_DELETED: 'gps:task:deleted',
  TASK_COMPLETED: 'gps:task:completed',
  TASK_OVERDUE: 'gps:task:overdue',
  
  // Productivity Coaching Events
  AGENT_CREATED: 'coaching:agent:created',
  AGENT_UPDATED: 'coaching:agent:updated',
  AGENT_MILESTONE_COMPLETED: 'coaching:agent:milestone:completed',
  AGENT_NEEDS_FOLLOWUP: 'coaching:agent:needs_followup',
  
  TRANSACTION_CREATED: 'coaching:transaction:created', 
  TRANSACTION_UPDATED: 'coaching:transaction:updated',
  TRANSACTION_COMPLETED: 'coaching:transaction:completed',
  TRANSACTION_FAILED: 'coaching:transaction:failed',
  
  CLASS_SCHEDULED: 'coaching:class:scheduled',
  CLASS_COMPLETED: 'coaching:class:completed',
  CLASS_CANCELLED: 'coaching:class:cancelled',
  
  COMMUNICATION_SENT: 'coaching:communication:sent',
  COMMUNICATION_RECEIVED: 'coaching:communication:received',
  
  // Module Events
  MODULE_LOADED: 'module:loaded',
  MODULE_UNLOADED: 'module:unloaded',
  MODULE_ERROR: 'module:error',
  
  // UI Events
  NOTIFICATION_SHOW: 'ui:notification:show',
  MODAL_OPEN: 'ui:modal:open',
  MODAL_CLOSE: 'ui:modal:close',
  PAGE_CHANGED: 'ui:page:changed',
  
  // Data Events
  DATA_SYNC_START: 'data:sync:start',
  DATA_SYNC_COMPLETE: 'data:sync:complete',
  DATA_SYNC_ERROR: 'data:sync:error',
  
  // AI Events
  AI_QUERY: 'ai:query',
  AI_RESPONSE: 'ai:response',
  AI_RECOMMENDATION: 'ai:recommendation'
};

// Helper functions for common patterns
export const createModuleEventHandler = (moduleName) => {
  return {
    emit: (eventType, data, options = {}) => 
      eventBus.emit(eventType, data, { ...options, source: moduleName }),
    
    on: (eventType, callback, options = {}) => 
      eventBus.on(eventType, callback, { ...options, module: moduleName }),
    
    once: (eventType, callback, options = {}) => 
      eventBus.once(eventType, callback, { ...options, module: moduleName })
  };
};

// React hook for using event bus in components
export const useEventBus = (moduleName = 'component') => {
  const React = require('react');
  const { useEffect, useCallback } = React;
  
  const emit = useCallback((eventType, data, options = {}) => {
    return eventBus.emit(eventType, data, { ...options, source: moduleName });
  }, [moduleName]);
  
  const on = useCallback((eventType, callback, options = {}) => {
    return eventBus.on(eventType, callback, { ...options, module: moduleName });
  }, [moduleName]);
  
  const once = useCallback((eventType, callback, options = {}) => {
    return eventBus.once(eventType, callback, { ...options, module: moduleName });
  }, [moduleName]);
  
  // Auto-cleanup on unmount
  useEffect(() => {
    const subscriptions = [];
    
    return () => {
      // Clean up subscriptions when component unmounts
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, []);
  
  return { emit, on, once };
};

export default eventBus;
